// Operator logic split out of boot.ts so it is UNIT-TESTABLE without boot.ts's side-effects (boot.ts is the
// composition root: self-check → bootstrap → wire these into window.CPDice). Pure mapping + the SDK-driven round
// driver / verifier factories. Owns ZERO outcome/money math — the money is the server's (res.payoutMinor), k is
// re-derived from the reveal via the CONTRACT (pf/verify.ts → @blocklabsdev/rgs-contracts diceResolve).

import type { GameSdk, PfRoundEvidence } from "@blocklabsdev/rgs-game-manager-frontend-api";
import { checkSettledRound, verifyRound, type VerifyInput, type VerifyReport } from "./pf/verify";
import { resolve, type Sel } from "./contract";
import { classifyDenial, TIMED_OUT, UNCONFIRMED } from "./round-outcome";

// The vendor's frozen round `r` (the-handles.js doRoll(): { mode, tH, aH, bH, cU, stake, nonce, server, client }) → the
// contract's Sel. under/over carry a single threshold (tH); between/outside carry the half-open band [aH, bH) —
// the SAME field mapping the-handles.js's own winsIn(r, k) uses, so a driver-supplied k and the vendor's local settle()
// agree on the same round.
export function selFromFrozen(r: any): Sel {
  if (r.mode === "under" || r.mode === "over") return { mode: r.mode, target: r.tH };
  return { mode: r.mode, lo: r.aH, hi: r.bH }; // between | outside
}

// The FLAT, per-mode selection payload for the SDK's play() (Record<string, string|number|boolean> — no optional/
// undefined fields). under/over → {mode,target}; between/outside → {mode,lo,hi}. The fake + the contract's
// diceResolve read exactly those fields per mode.
export function playSelection(sel: Sel): Record<string, string | number> {
  return sel.mode === "under" || sel.mode === "over"
    ? { mode: sel.mode, target: sel.target as number }
    : { mode: sel.mode, lo: sel.lo as number, hi: sel.hi as number };
}

/** One settled, VERIFIED round as the game showed it — the provably-fair panel's record (SC-61813). `ref` is the round's
 *  number in this session: the history row and the panel name the same round with it (the RGS mints a fresh seed pair
 *  per round, so its revealed nonce is not a round identifier). */
export interface RoundRecord extends VerifyInput {
  readonly ref: number;
  readonly roundId: string;
  /** The id the game played the round under (the SDK's clientRoundId): a pf.proof names its round with it. */
  readonly clientRoundId: string;
  /** A round played on a pf ticket (SC-65843): its evidence — what the device held before the bet, and the reveal. The
   *  panel checks it with the contract's verifier (pf/ticket-verify.ts); absent for a legacy round. */
  readonly pf?: PfRoundEvidence;
}

/** How many settled rounds are kept for verification: exactly as many as the vendored history keeps (its ROUNDS_MAX —
 *  pinned by a test), so every roll a history row can show can still be verified. */
export const ROUND_RECORD_LIMIT = 400;

export interface DriverDeps {
  sdk: Pick<GameSdk, "play" | "reportResultsShown">;
  /** the session currency, read fresh each round (set from session.ready/restored). */
  getCurrency: () => string;
  /** the verified rounds of this session, keyed by ref (the provably-fair panel reads them). */
  records: Map<number, RoundRecord>;
  /** Why no round may be sent right now, or null when one may: the SDK boot loader (or its error surface) or a fatal
   *  notice covers the board, the document is rebooting (BOARD_NOT_READY), or the operator casino holds the game
   *  (OPERATOR_HOLD, SC-61815). Defence in depth behind boot.ts keeping the game paused meanwhile: a round the player
   *  cannot see, or one the operator stopped, is never sent. */
  refusal?: () => string | null;
  /** A SETTLED round failed verification (SC-61813): the money moved server-side, but its reveal is missing, malformed
   *  or does not match its commitment (or the credited amount is not a usable money value), so no roll may be shown for
   *  it. boot.ts stops the game and asks for a reload (which re-reads the balance). */
  onUnverifiable?: (info: { readonly roundId: string; readonly reason: string }) => void;
  /** A round's OUTCOME IS UNKNOWN (SC-61817, round-outcome.ts): it was neither settled nor refused with a code that
   *  proves no money moved, or nothing came back in time. The server may have debited, settled or refunded it, so the
   *  game must not go on with a balance it cannot vouch for: boot.ts stops it and asks for a reload (which re-reads
   *  the balance). The round is never sent again — a new attempt would be a second wager. */
  onUnconfirmed?: (info: { readonly code: string }) => void;
  /** The wallet refused the round (no money moved): the reason the game shows (round-outcome.ts). The game's own rail
   *  shows it through the seam (RoundRefused.playerMessage); this is for what the operator shows itself (the toast on a
   *  screen too short for that rail, SC-61819). */
  onRefused?: (info: { readonly code: string; readonly message: string }) => void;
  /** Money in the session's currency, for the words a refusal says (SC-64981: the max stake the RGS states). */
  formatMinor?: (minor: number) => string;
  /** SC-64981: hands the max stake the RGS stated for a selection winning on `winCount` rolls to the game's ceiling
   *  (./max-stake stated) — before the refusal reaches the game, whose repaint clamps the stake to it — and returns the
   *  ceiling now held for that selection, which is what the words say: never above the terms' own figure, never raised by
   *  a later answer (null: none held). */
  holdMaxStake?: (winCount: number, maxStakeMinor: number) => number | null;
  /** SC-68493: the game's hundredths -> the wire's minor units (the bet), and a payout back, exactly or not at all
   *  (currency.ts payoutToGame). Absent: the same units (a two-decimal currency). */
  toWire?: (gameMinor: number) => number;
  payoutToGame?: (wireMinor: number) => number | undefined;
  /** The round SETTLED (SC-62290): `balanceStale` when the SDK could not confirm the balance it holds for it (no
   *  balance on the wire and its re-read failed), so the board must not be corrected to that balance when the roll
   *  lands (balance-sync.ts). Told before the roll is verified or shown — the money moved either way. */
  onSettled?: (round: { readonly balanceStale?: true }) => void;
  /** The largest stake the RGS admits for a selection winning on `winCount` rolls (SC-64981, ./max-stake forBet), read where
   *  the roll is sent; null when no ceiling is known; "unconfirmed" when the ticket the bet will use could not be confirmed
   *  (SC-67503). A stake above it, or with its terms unconfirmed, is never sent. */
  maxStakeForBet?: (winCount: number) => Promise<number | null | "unconfirmed" | "math_unknown">;
  /** How long a round may take before its outcome is treated as unknown (default ROUND_DEADLINE_MS). */
  roundDeadlineMs?: number;
  /** True while the page is leaving its launch (SC-61840: a reload is under way, the SDK was stopped). A round refused
   *  or cut then is not the player's to retry: the game is told the page is reloading (LEAVING_MESSAGE), never left to
   *  its generic "Round failed. Try again." */
  leaving?: () => boolean;
}

/** The game's own bound on a round — a backstop: since SDK 0.13.0 (SC-61804) a REAL play() resolves `round_failed`
 *  within about 16 s on its own (the gateway gives a round 10 s), so this is reached only if the SDK itself never
 *  answers. Generous on purpose — past it the player is better served by a re-read balance than by a spinner. */
export const ROUND_DEADLINE_MS = 30_000;

/** What the game's rail says for a round refused or cut while the page is leaving its launch. */
export const LEAVING_MESSAGE = "Reloading…";
/** What the game says for a stake above the RGS's max stake for the selection, caught where the roll is sent (SC-64981):
 *  nothing was sent, and the stake on the board is lowered to the ceiling the RGS admits for the player to roll again. */
export const STAKE_ABOVE_MAX_MESSAGE = "Above the max stake for this selection. Check the stake and roll again";
/** SC-68793: what the game's rail says for a roll on a ticket this build cannot verify. Nothing was sent. */
export const TICKET_MATH_UNKNOWN_MESSAGE = "Can't verify this ticket. No bet placed. Reload the game";
/** SC-67503: the terms of the ticket the roll would be made on could not be confirmed (no usable ticket, or no answer in
 *  time): nothing was sent. */
export const TERMS_UNCONFIRMED_MESSAGE = "This roll's terms could not be confirmed. Roll again";

/** A max stake the RGS stated that can be used: a whole number of minor units, at least one. */
const statedMaxStake = (v: unknown): number | undefined => (typeof v === "number" && Number.isSafeInteger(v) && v >= 1 ? v : undefined);

/** Why the driver did not hand the game a roll. `playerMessage` is the reason the game shows where it shows its own
 *  stake messages (the seam reads it); without one the game says its generic line. */
export class RoundRefused extends Error {
  readonly playerMessage: string | undefined;
  constructor(code: string, playerMessage?: string) {
    super(code);
    this.name = "RoundRefused";
    this.playerMessage = playerMessage;
  }
}

/** `work`, or `onExpire()`'s error once `ms` have passed. A late `work` settles nothing (and is never unhandled). */
function within<T>(work: Promise<T>, ms: number, onExpire: () => Error): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(onExpire()), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

// The round driver installed into window.CPDice.setRollDriver. Maps the vendor's frozen round → RGS play and
// returns { k, payoutMinor, ref }:
//   - payoutMinor is the SERVER's floor-toward-house payout, RETURNED VERBATIM (RULING R3; in the game's hundredths, an exact
//     conversion for USDT, SC-68493 currency.ts) — the-handles.js's settle()
//     credits THIS, never its local round-half-up payoutCents. Never re-derived here.
//   - k is re-derived from the reveal and returned ONLY once the reveal backs the roll (SC-61813): it is well-formed and
//     its seed hashes to the commitment. Otherwise the game would animate a roll it cannot prove (a default k=0 used to
//     show a WIN or a LOSS for a round with no usable reveal): onUnverifiable stops the game and the driver rejects, so
//     nothing is animated. The AMOUNT stays the server's: a limit, a bonus or a denomination may make it differ from
//     the roll's own payout, in size and even in sign, and the game cannot tell from the wire (pf/verify.ts).
//   - ref numbers the round in this session; the seam writes it on the round so the history row names it.
// A round that does not settle rejects (RoundRefused) → the vendor's own doRoll rejection branch: no debit, nothing
// animated. What the game does next depends on what the answer PROVES (SC-61817, round-outcome.ts):
//   - refused before play() (refusal(): BOARD_NOT_READY, OPERATOR_HOLD) — nothing was sent;
//   - refused by the wallet (insufficient_funds, …) — no money moved: the reason is shown and play goes on;
//   - the session is over — the SDK's error.fatal already stopped the game (boot.ts);
//   - anything else, a thrown play(), or no answer within the deadline — the OUTCOME IS UNKNOWN: onUnconfirmed stops
//     the game. Each attempt has its own round id and is never sent again; an answer that arrives after the deadline
//     is dropped (nothing shown, nothing recorded) — the reload's balance read is what the player gets.
export function makeRollDriver(deps: DriverDeps) {
  let nextRef = 1;
  const toWire = deps.toWire ?? ((m: number) => m);
  const payoutToGame = deps.payoutToGame ?? ((m: number): number | undefined => m);
  return async (r: any): Promise<{ k: number; payoutMinor: number; ref: number }> => {
    // A refusal with no reason of its own to show: silent (the game's generic line) — unless the page is leaving.
    const withoutReason = (code: string): RoundRefused => new RoundRefused(code, deps.leaving?.() ? LEAVING_MESSAGE : undefined);
    const refused = deps.refusal?.() ?? null;
    if (refused !== null) throw withoutReason(refused);
    const sel = selFromFrozen(r);
    // SC-64981: never send a stake above the RGS's max stake for the selection, read again here — the ticket the SDK will
    // use may have arrived (or changed) since the game froze this stake. Refused before anything is sent: no money moved.
    const winCount = resolve(0, 1n, sel).winCount;
    if (deps.maxStakeForBet) {
      const max = await deps.maxStakeForBet(winCount);
      if (max === "unconfirmed") throw new RoundRefused("TERMS_UNCONFIRMED", TERMS_UNCONFIRMED_MESSAGE);
      if (max === "math_unknown") throw new RoundRefused("PF_TICKET_MATH_UNKNOWN", TICKET_MATH_UNKNOWN_MESSAGE); // SC-68793
      if (max !== null && r.stake > max) throw new RoundRefused("STAKE_ABOVE_MAX", STAKE_ABOVE_MAX_MESSAGE);
      const after = deps.refusal?.() ?? null; // it may have waited for the first ticket: the board is asked again
      if (after !== null) throw withoutReason(after);
    }
    const clientRoundId = crypto.randomUUID();

    let abandoned = false; // the deadline passed: the game was stopped, whatever comes back is dropped
    const unconfirmed = (code: string): RoundRefused => {
      console.error("[dice] a round's outcome is unknown; the game stops:", code);
      deps.onUnconfirmed?.({ code });
      return withoutReason("ROUND_UNCONFIRMED");
    };
    const dropped = (): RoundRefused => new RoundRefused("ROUND_ABANDONED");

    const round = (async () => {
      let res: Awaited<ReturnType<typeof deps.sdk.play>>;
      try {
        res = await deps.sdk.play({
          clientRoundId,
          bet: { amountMinor: toWire(r.stake), currency: deps.getCurrency() },
          selection: playSelection(sel),
        });
      } catch {
        // play() never rejects by contract; if it does, nothing is known about the round
        throw abandoned ? dropped() : unconfirmed(UNCONFIRMED);
      }
      if (abandoned) throw dropped();
      if (res.status !== "settled") {
        // SC-62233: the platform's moneyMoved, when it states one, is the proof (round-outcome.ts)
        const denial = classifyDenial((res as { code?: unknown }).code, (res as { moneyMoved?: unknown }).moneyMoved);
        if (denial.kind === "unconfirmed") throw unconfirmed(denial.code);
        if (denial.kind === "session") throw new RoundRefused(denial.code);
        // SC-64981: the RGS states the largest stake that fits this selection — handed to the game's ceiling for the board to
        // clamp to, and the words say the ceiling held after it (never this answer's own figure: the ticket's terms can
        // change while a roll is out, and the ceiling never goes above theirs)
        const said = denial.code === "max_win_exceeded" ? statedMaxStake((res as { maxStakeMinor?: unknown }).maxStakeMinor) : undefined;
        const held = said === undefined ? undefined : (deps.holdMaxStake?.(winCount, said) ?? said);
        const message = held !== undefined && deps.formatMinor ? `Max stake for this selection: ${deps.formatMinor(held)}` : denial.message;
        deps.onRefused?.({ code: denial.code, message });
        throw new RoundRefused(denial.code, message);
      }
      deps.onSettled?.(res.balanceStale === true ? { balanceStale: true } : {});
      // The server's payout, in the game's hundredths: the round's credits (currency.ts), what the roll's replay counts in.
      // One the game cannot show exactly is not shown at all: the money moved server-side, the game stops (as for a round
      // it cannot verify) and the reload re-reads the balance.
      const payoutMinor = payoutToGame(res.payoutMinor);
      if (payoutMinor === undefined) {
        console.error("[dice] a settled payout is not a whole number of the game's hundredths; the game stops:", res.roundId);
        deps.onUnverifiable?.({ roundId: res.roundId, reason: "payout_not_whole_hundredths" });
        throw new RoundRefused("UNVERIFIABLE_ROUND");
      }

      const check = await checkSettledRound(res.fairness, sel, r.stake, payoutMinor);
      if (abandoned) throw dropped();
      if (!check.ok) {
        console.error("[dice] a settled round failed verification; the game stops:", check.reason, res.roundId);
        deps.onUnverifiable?.({ roundId: res.roundId, reason: check.reason });
        throw new RoundRefused("UNVERIFIABLE_ROUND");
      }
      deps.sdk.reportResultsShown({ roundId: res.roundId });
      const ref = nextRef++;
      deps.records.set(ref, {
        ref,
        roundId: res.roundId,
        clientRoundId,
        ...(res.pf !== undefined ? { pf: res.pf } : {}),
        fairness: res.fairness!,
        sel,
        betMinor: r.stake,
        expected: { k: check.k, result: check.result, payoutMinor },
      });
      if (deps.records.size > ROUND_RECORD_LIMIT) deps.records.delete(ref - ROUND_RECORD_LIMIT);
      return { k: check.k, payoutMinor, ref };
    })();

    return within(round, deps.roundDeadlineMs ?? ROUND_DEADLINE_MS, () => {
      abandoned = true;
      return unconfirmed(TIMED_OUT);
    });
  };
}

// The verifier installed into window.CPDice.setVerifier: look the round up by its ref (the number the history row and
// the panel show) and replay it through OUR RT-4 derivation (verifyRound) — commitment + roll + payout.
export function makeVerifier(records: Map<number, RoundRecord>) {
  return (ref: number): Promise<VerifyReport | null> => {
    const rec = records.get(ref);
    return rec ? verifyRound(rec) : Promise.resolve(null);
  };
}
